import { useCallback, useEffect, useRef, useState } from 'react';
import { matchVoiceCommand, mergeCapabilityPatch } from './voiceIntent';
import { matchAskOption } from './matchAskOption';
import {
  shouldListen, dockStateFor, voiceErrorCodeFor, frameLevel, meterUtterance, detectBargeIn,
  VOICE_METER,
} from './voiceLoop';
import { needsPolish } from './transcriptPolish';
const REARM_DELAY_MS = 400;
// A voice dock that shows no activity for this long is wedged, not waiting — say so
// rather than sitting on "Voice mode on — speak anytime" forever (the original bug).
const WATCHDOG_MS = 3000;

/**
 * Hands-free voice "call" mode — manages MediaRecorder capture with Web Audio
 * silence detection, transcribes via the server STT endpoint, optionally
 * corrects the transcript via a cheap LLM call, then either:
 *  1. Matches a voice command (auto-approve, retrieval, tools) on the RAW
 *     transcript → applies it locally and speaks a short confirmation.
 *  2. Matches an ask_user question's options on the corrected transcript →
 *     answers via submitAnswer.
 *  3. Falls through → auto-sends the corrected transcript as a chat message.
 *
 * When voice mode is on, the hook runs a continuous loop: listen → silence →
 * transcribe → correct → dispatch → wait for reply → listen again. If an
 * ask_user question appears, the hook speaks it aloud and then listens for
 * the spoken answer.
 *
 * The loop itself never arms while the agent is thinking or speaking (echo guard), with
 * one deliberate exception: a separate watcher listens THROUGH the agent's own voice so
 * the user can talk over it, and cuts the reply off when they do (see the barge-in
 * effect). Correction is skipped for transcripts that don't need it (needsPolish).
 *
 * The microphone is acquired ONCE, in the click that enters voice mode, and held
 * for the whole call (each utterance is a fresh MediaRecorder over the retained
 * stream). Two reasons, both bugs this replaces: a getUserMedia issued later from
 * a timer has no user activation behind it, and an AudioContext built without that
 * activation stays suspended — its analyser then reads a flat 128 baseline, which
 * looks like permanent silence and cuts every utterance at SILENCE_DURATION_MS.
 *
 * All external actions (speak, send, answerQuestion, correct) are injected
 * by the caller so this hook stays agnostic of the auth layer (internal vs embed).
 *
 * @param {{
 *   speak: (text: string) => Promise<void>,
 *   cancel: () => void,
 *   send: (text: string) => Promise<void>,
 *   answerQuestion: (payload: { selectedValue?: string, freeText?: string }) => Promise<void>,
 *   correct?: (text: string) => Promise<string>,
 *   pendingJob: { id: string|null, status?: string, question?: { question: string, options: Array<{value:string,label:string}> } } | null,
 *   approvalMode: string,
 *   setApprovalMode?: (mode: string) => void,
 *   transcribe: (blob: Blob) => Promise<string>,
 *   serverSttReady: boolean,
 *   sending?: boolean,
 *   speaking?: boolean,
 * }} deps
 */
export function useVoiceMode({
  speak,
  cancel,
  send,
  answerQuestion,
  correct,
  pendingJob,
  approvalMode: _approvalMode,
  setApprovalMode,
  transcribe,
  serverSttReady: _serverSttReady,
  sending = false,
  speaking = false,
}) {
  const [voiceMode, setVoiceMode] = useState(false);
  // The held stream exists and the analyser is running — the loop must not try to
  // record before this, since there is no device to record from yet.
  const [micReady, setMicReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  // getUserMedia in flight — the permission prompt may be pending, in which case
  // neither the success nor the catch path has run yet. Without this the dock
  // sat on "Voice mode on — speak anytime" with no prompt and no error.
  const [arming, setArming] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [lastUtterance, setLastUtterance] = useState(null);
  // Why the voice loop last stopped, as a code from voiceLoop's VOICE_ERROR_COPY —
  // the dock renders the copy. Without this every failure (denied mic, 424 no STT
  // key, 429 rate limit) was a silent no-op: the mic appeared to do nothing.
  const [lastError, setLastError] = useState(null);

  // { stream, audioCtx, analyser } for the whole call — NOT per utterance.
  const sessionRef = useRef(null);
  const recorderRef = useRef(null);
  const silenceStartRef = useRef(null);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const processedRef = useRef(false);
  // Did anything in this utterance rise above the noise floor? A capture that never
  // did is dropped before the network: transcribing pure silence spent an STT call
  // (and a slice of the 40/min ':stt' bucket) every re-arm, which rate-limited the
  // call all by itself.
  const sawSpeechRef = useRef(false);
  const pendingPatchRef = useRef({});
  const spokenQuestionIdRef = useRef(null);
  const loopTimerRef = useRef(null);
  const startCaptureRef = useRef(null);
  // When the current reply began playing — the barge-in grace window is measured from it.
  const speechStartedAtRef = useRef(null);

  /** Stops the current utterance's recorder + meter. Leaves the mic session alive. */
  const stopCapture = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setArming(false);
    setCapturing(false);
  }, []);

  /** Ends the call's mic session — the only place the stream/context are torn down. */
  const releaseMic = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      session.stream.getTracks().forEach((t) => t.stop());
      session.audioCtx.close().catch(() => {});
    }
    setMicReady(false);
  }, []);

  /** Opens the mic for the call. Must be invoked from the entering click (see header). */
  const acquireMic = useCallback(async () => {
    if (sessionRef.current) return sessionRef.current;

    // Secure-context guard: on plain HTTP (or an old WebView) mediaDevices is
    // simply absent — classify it instead of throwing into a silent catch.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia
      || typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
      setArming(false);
      setLastError('mic_unsupported');
      return null;
    }

    try {
      // Echo cancellation is what makes listening during playback viable at all, and
      // holding one stream for the whole call is what makes it possible to ask for it.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new Ctx();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      sessionRef.current = { stream, audioCtx, analyser };
      setArming(false);
      setMicReady(true);
      return sessionRef.current;
    } catch (err) {
      // A denied/blocked mic rejects from getUserMedia with no prompt at all —
      // say so in the console as well as the dock (matching the transcribe path).
      console.warn('[voice] microphone start failed:', err?.name || err?.message || err);
      setArming(false);
      setMicReady(false);
      setLastError(voiceErrorCodeFor(err, 'capture'));
      return null;
    }
  }, []);

  const processTranscript = useCallback(async (rawText) => {
    if (!rawText || !rawText.trim()) return;

    // 1. Voice command check — always on the RAW transcript (commands must
    //    not be mangled by LLM correction)
    const cmd = matchVoiceCommand(rawText);
    if (cmd) {
      if (cmd.type === 'approval' && cmd.patch.approvalMode && setApprovalMode) {
        setApprovalMode(cmd.patch.approvalMode);
      }
      pendingPatchRef.current = mergeCapabilityPatch(pendingPatchRef.current, cmd.patch);
      await speak(cmd.confirm);
      return;
    }

    // 2. LLM correction — only for transcripts that look like they need it, so a
    //    spoken "yes" is not held up by a model round trip (see transcriptPolish.js)
    let text = rawText.trim();
    if (correct && needsPolish(text)) {
      setCorrecting(true);
      try {
        text = await correct(rawText);
      } catch { /* fail-soft: use raw */ }
      setCorrecting(false);
    }

    setLastUtterance(text);

    // 3. If a question is pending, try to match an option
    if (pendingJob?.question) {
      const match = matchAskOption(text, pendingJob.question);
      await answerQuestion(match);
      return;
    }

    // 4. Normal message — auto-send
    try {
      await send(text);
    } catch (err) {
      console.warn('[voice] send failed:', err?.message || err);
      setLastError('send_failed');
    }
  }, [speak, pendingJob, answerQuestion, send, setApprovalMode, correct]);

  /** Records one utterance over the call's held stream. */
  const startCapture = useCallback(async () => {
    if (capturing || transcribing || correcting || arming) return;
    const session = sessionRef.current;
    // The loop gates on micReady, so this is only a race with hang-up — no error.
    if (!session) return;

    // A fresh attempt is the user's retry — clear the previous failure so the
    // dock shows "Listening…" rather than a stale error.
    setLastError(null);
    processedRef.current = false;
    sawSpeechRef.current = false;
    chunksRef.current = [];

    let recorder;
    try {
      recorder = new window.MediaRecorder(session.stream);
    } catch (err) {
      console.warn('[voice] recorder start failed:', err?.name || err?.message || err);
      setLastError(voiceErrorCodeFor(err, 'capture'));
      return;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };

    recorder.onstop = async () => {
      if (processedRef.current) return;
      processedRef.current = true;
      stopCapture();

      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed < VOICE_METER.MIN_UTTERANCE_MS) return;
      if (!sawSpeechRef.current) return; // nothing was said — re-arm locally, spend nothing

      const mimeType = recorder.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (!blob.size) return;

      setTranscribing(true);
      try {
        const text = await transcribe(blob);
        await processTranscript(text);
      } catch (err) {
        // Transcription failed — surface WHY (no STT key, rate limit, provider
        // error) instead of the old silent no-op, and stop the auto-retry loop.
        console.warn('[voice] transcription failed:', err?.message || err);
        setLastError(voiceErrorCodeFor(err, 'transcribe'));
      } finally {
        setTranscribing(false);
      }
    };

    // Barge-in: cancel any in-progress TTS
    cancel();

    recorder.start(100);
    startTimeRef.current = Date.now();
    setArming(false);
    setCapturing(true);
    silenceStartRef.current = null;

    const { analyser } = session;
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      const verdict = meterUtterance({
        level: frameLevel(data),
        now: Date.now(),
        startedAt: startTimeRef.current,
        silenceStartedAt: silenceStartRef.current,
      });
      if (verdict.speech) sawSpeechRef.current = true;
      silenceStartRef.current = verdict.silenceStartedAt;
      if (verdict.stop) {
        stopCapture();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [capturing, transcribing, correcting, arming, cancel, transcribe, processTranscript, stopCapture]);

  // The loop reaches startCapture through this ref, never through its dependency
  // array — see the loop effect for why that distinction is the whole fix.
  useEffect(() => { startCaptureRef.current = startCapture; });

  // Cleanup on unmount
  useEffect(() => () => {
    processedRef.current = true; // a recorder stopping during teardown must not set state
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (loopTimerRef.current) clearTimeout(loopTimerRef.current);
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      session.stream.getTracks().forEach((t) => t.stop());
      session.audioCtx.close().catch(() => {});
    }
  }, []);

  // Leaving voice mode by any route (hang-up button, host, unmount-adjacent state
  // change) ends the call: no pending re-arm, no recorder, no held mic.
  useEffect(() => {
    if (voiceMode) return;
    if (loopTimerRef.current) { clearTimeout(loopTimerRef.current); loopTimerRef.current = null; }
    if (capturing || arming) { processedRef.current = true; stopCapture(); }
    if (sessionRef.current) releaseMic();
  }, [voiceMode, capturing, arming, stopCapture, releaseMic]);

  // Continuous loop: re-arm capture when the agent finishes speaking/thinking.
  // A surfaced error (lastError) deliberately halts the loop — see shouldListen.
  //
  // Every dependency here is a PRIMITIVE, and a countdown already in flight is left
  // alone rather than cancelled and rescheduled. Both matter: this effect used to
  // depend on `startCapture`, whose identity changes on every render (the callers
  // inject fresh closures), and its cleanup cleared the pending timer — so on any
  // surface rendering faster than REARM_DELAY_MS the mic was never requested at
  // all, and the dock sat on "Voice mode on — speak anytime" with no error.
  useEffect(() => {
    const want = shouldListen({
      voiceMode,
      micReady,
      capturing,
      arming,
      transcribing,
      sending,
      speaking,
      pendingStatus: pendingJob?.status || null,
      error: lastError,
    });
    if (!want) {
      if (loopTimerRef.current) { clearTimeout(loopTimerRef.current); loopTimerRef.current = null; }
      return;
    }
    if (loopTimerRef.current) return;
    // Small debounce after TTS ends to avoid picking up speaker tail
    loopTimerRef.current = setTimeout(() => {
      loopTimerRef.current = null;
      if (startCaptureRef.current) startCaptureRef.current();
    }, REARM_DELAY_MS);
  }, [voiceMode, micReady, capturing, arming, transcribing, sending, speaking, pendingJob?.status, lastError]);

  useEffect(() => {
    if (!speaking) { speechStartedAtRef.current = null; return; }
    if (speechStartedAtRef.current == null) speechStartedAtRef.current = Date.now();
  }, [speaking]);

  // Barge-in: listen THROUGH the agent's own voice so the user can talk over it, and cut
  // the reply off when they do. This is the one deliberate exception to the echo guard —
  // shouldListen() still refuses to arm while `speaking`, and this watcher overrides it
  // rather than relaxing it, so the loop's state table stays unchanged.
  //
  // It meters the analyser that is already open on the held stream (no second
  // getUserMedia, no second AudioContext) and hands off to startCapture, which cancels
  // the in-flight TTS itself. Its rAF handle is deliberately local, not the capture
  // meter's rafRef, so stopping one never stops the other.
  useEffect(() => {
    if (!voiceMode || !micReady || !speaking) return undefined;
    if (capturing || arming || transcribing || correcting || lastError) return undefined;
    const session = sessionRef.current;
    if (!session) return undefined;

    const data = new Uint8Array(session.analyser.fftSize);
    let raf = null;
    let loudSince = null;
    let stopped = false;
    const watch = () => {
      if (stopped) return;
      session.analyser.getByteTimeDomainData(data);
      const verdict = detectBargeIn({
        level: frameLevel(data),
        now: Date.now(),
        loudSince,
        speechStartedAt: speechStartedAtRef.current,
      });
      loudSince = verdict.loudSince;
      if (verdict.bargeIn) {
        if (startCaptureRef.current) startCaptureRef.current();
        return;
      }
      raf = requestAnimationFrame(watch);
    };
    raf = requestAnimationFrame(watch);
    return () => { stopped = true; if (raf) cancelAnimationFrame(raf); };
  }, [voiceMode, micReady, speaking, capturing, arming, transcribing, correcting, lastError]);

  // Watchdog: voice mode on, nothing happening, no error — the loop is wedged.
  // Report it instead of leaving the user talking to a dead dock.
  useEffect(() => {
    if (!voiceMode || lastError) return undefined;
    if (capturing || arming || transcribing || correcting || sending || speaking) return undefined;
    if (pendingJob?.status === 'waiting_for_client') return undefined;
    const timer = setTimeout(() => setLastError('mic_failed'), WATCHDOG_MS);
    return () => clearTimeout(timer);
  }, [voiceMode, micReady, capturing, arming, transcribing, correcting, sending, speaking, pendingJob?.status, lastError]);

  // Speak ask_user questions aloud when they first appear (eyes-free)
  useEffect(() => {
    if (!voiceMode) return;
    const questionId = pendingJob?.id;
    const question = pendingJob?.question;
    if (pendingJob?.status === 'waiting_for_user' && question && questionId !== spokenQuestionIdRef.current) {
      spokenQuestionIdRef.current = questionId;
      const optionsText = (question.options || []).map((o, i) => `Option ${i + 1}: ${o.label}`).join('. ');
      const prompt = `${question.question}. ${optionsText}. Say option one, two, or your answer.`;
      speak(prompt).catch(() => {});
    }
  }, [voiceMode, pendingJob, speak]);

  const toggleVoiceMode = useCallback(() => {
    if (voiceMode) {
      setVoiceMode(false);
      setLastError(null);
      // Claim the in-flight utterance before stopping: recorder.stop() fires onstop,
      // and hanging up mid-sentence must not still transcribe and send it.
      processedRef.current = true;
      stopCapture();
      releaseMic();
      return;
    }
    // Entering voice mode clears the last failure, so the next entry re-arms the
    // loop even after a rate-limit or denied-mic stop.
    setLastError(null);
    setVoiceMode(true);
    setArming(true);
    // Started synchronously in the click handler on purpose — a getUserMedia issued
    // later from a timer carries no user activation, and that is what left Chrome
    // never prompting and never lighting the capture indicator.
    acquireMic();
  }, [voiceMode, stopCapture, releaseMic, acquireMic]);

  /** Returns the accumulated per-turn capability patches and clears them. */
  const consumePendingPatch = useCallback(() => {
    const patch = { ...pendingPatchRef.current };
    pendingPatchRef.current = {};
    return patch;
  }, []);

  const dockState = dockStateFor({
    voiceMode,
    capturing,
    arming,
    transcribing,
    correcting,
    sending,
    speaking,
    pendingStatus: pendingJob?.status || null,
    lastUtterance,
    error: lastError,
  });

  return {
    voiceMode,
    toggleVoiceMode,
    micReady,
    capturing,
    arming,
    transcribing,
    correcting,
    dockState,
    lastUtterance,
    lastError,
    startCapture,
    stopCapture,
    consumePendingPatch,
    pendingVoiceCommand: null,
  };
}

export default useVoiceMode;
